// Outbound calling API (CU-869fm5hj1). Backend: B6 (CU-869fk1hqc), contract
// ~/pm/contracts/B6.md v1 (2026-10-24 early). Every URL and response shape the
// UI relies on lives in this file.
import { callApi } from '../../utils/apiCall';

const practice = (p) => `/api/v1/practices/${p}`;

export const OUTBOUND_URLS = {
    // §3 GET (caller) → { desks: [{ id, name, extension, connection_state,
    //   held_by: { practice_user_id, name, since, is_me } | null }],
    //   mine: { desk_id, name, since } | null }. 403 = not a caller.
    desks: (p) => `${practice(p)}/voice-desks/`,
    // §3 POST { take_over } → 200 { desk_id, name, since } (also the heartbeat;
    //   releases any other desk I held) | 409 { code: 'desk_in_use', held_by }
    claim: (p, deskId) => `${practice(p)}/voice-desks/${deskId}/claim/`,
    // §3 POST → 204
    release: (p) => `${practice(p)}/voice-desks/release/`,
    // §4 POST { number, lead_id } → 202 { status: 'dialling', desk_id, … }
    //   | 200 { status: 'manual', number, reason, message } | 400 | 403
    //   | 502 { code, detail } (Elevate refused)
    dial: (p) => `${practice(p)}/voice-calls/dial/`,
    // V2-3 GET ?lead_id=&call=&number= (any member) → { route: 'elevate' | 'twilio' | 'none',
    //   elevate: { ready, reason, desk } | null, twilio: { phones: [...] } | null,
    //   prefill: { number, number_display, lead, call } } (contract ~/pm/contracts/V2-3.md v1)
    dialer: (p) => `${practice(p)}/voice-calls/dialer/`,
    // §5/§6 POST (transfer: { desk_id } | { number }) → 202 { status: 'sent' }
    //   | 409 { code: 'not_your_call' | 'call_not_active' | manual codes }
    //   | 501 { code: 'not_supported_by_elevate', detail } (hold / unhold)
    control: (p, callId, action) => `${practice(p)}/voice-calls/${callId}/${action}/`,
    // §2 POST (admin) → { authorize_url, expires_in } | 400 { code: 'not_configured' }
    connect: (p, connectionId, deskId) => `${practice(p)}/voice-connections/${connectionId}/desks/${deskId}/connect/`,
};

export const CONTROL_ACTIONS = Object.freeze(['hold', 'unhold', 'transfer', 'end']);

// Writes get a longer timeout: the backend calls Elevate before answering, and
// a timed-out POST may still have dialled (GOTCHAS: status 0 is not "failed").
const WRITE_OPTS = { timeout: 20000, suppressServerErrorDispatch: true };

export const fetchDesks = (practiceId) => callApi(OUTBOUND_URLS.desks(practiceId), 'GET', null, false, { suppressServerErrorDispatch: true });
export const claimDesk = (practiceId, deskId, { takeOver = false } = {}) => callApi(OUTBOUND_URLS.claim(practiceId, deskId), 'POST', { take_over: takeOver }, false, WRITE_OPTS);
// { reason: 'logout' } when the CRM signs the user out, so the desk history says so (CU-869fmd1vf).
export const releaseDesk = (practiceId, { reason } = {}) => callApi(OUTBOUND_URLS.release(practiceId), 'POST', reason ? { reason } : {}, false, WRITE_OPTS);
// The Idempotency-Key header isn't in B6 v1 yet; it is sent so the backend
// can start honouring it without a frontend change.
export const dial = (practiceId, { to, leadId, idempotencyKey }) => callApi(
    OUTBOUND_URLS.dial(practiceId), 'POST', { number: to, lead_id: leadId ?? null }, false,
    { ...WRITE_OPTS, headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {} },
);
export const fetchDialer = (practiceId, query = {}) => {
    const params = new URLSearchParams(query).toString();
    return callApi(`${OUTBOUND_URLS.dialer(practiceId)}${params ? `?${params}` : ''}`, 'GET', null, false, { suppressServerErrorDispatch: true });
};
export const controlCall = (practiceId, callId, action, body = {}) => {
    if (!CONTROL_ACTIONS.includes(action)) throw new Error(`Unknown call action: ${action}`);
    return callApi(OUTBOUND_URLS.control(practiceId, callId, action), 'POST', body, false, WRITE_OPTS);
};

// For the Desks tab (CU-869fm5hhx "Connect desk" / "Reconnect"): send the admin
// to Elevate's sign-in page for that desk. Elevate returns the browser to
// /dashboard/practice/integration?desk_connect=ok|error&desk_id=…
// Resolves { ok: true } after redirecting, or { ok: false, code } (e.g.
// 'not_configured' when the Elevate sign-in app isn't set up on the server;
// 'no_response' / 'forbidden' / 'not_found' when the server sent no code).
const CONNECT_STATUS_CODES = { 0: 'no_response', 403: 'forbidden', 404: 'not_found' };
export const connectDesk = async (practiceId, connectionId, deskId, assign = (url) => window.location.assign(url)) => {
    const res = await callApi(OUTBOUND_URLS.connect(practiceId, connectionId, deskId), 'POST', {}, false, WRITE_OPTS);
    const url = res?.ok ? res.data?.authorize_url : null;
    if (typeof url !== 'string' || !url.startsWith('https://')) {
        return { ok: false, code: res?.data?.code || CONNECT_STATUS_CODES[res?.status ?? 0] || 'no_url' };
    }
    assign(url);
    return { ok: true };
};
